/**
 * OrderDetailsSection — the Billing step's "Details" box.
 *
 * Was `ExternalReportingSection`. The external-reporting field is gone: legacy
 * only ever renders it when the client's organization carries an
 * EXTERNAL_REPORTING instruction product (`ValidationController` gates it on
 * `externalReportingInstruction`), and v2 has no signal for that on the wire.
 * So v2 offered it to everyone, which is worse than not offering it —
 * it invites data nobody consumes. The store field and the wire slot are left
 * alone; nothing writes them now.
 *
 * The reference is the one printed on the invoice, so it is labelled "Invoice reference" with no note; the
 * dashboard titles an order by its property, not by this field. Own keys, because legacy's shared
 * `purchase_flow:billing_step` copy is live in production.
 */

import React from 'react'
import { Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { MUIInputField } from 'components/common/MUIInputField'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'

import { MAX_REFERENCE_CHARACTERS, sanitizeReference } from '../billingDefaults'

interface Props {
  reference: string | undefined
  /** Owner of the property, shown on the invoice (legacy had this field
   *  and the v2 wire always had the slot; only the UI was missing). */
  propertyOwner?: string
  onReferenceChange: (next: string | undefined) => void
  onPropertyOwnerChange?: (next: string | undefined) => void
}

export const OrderDetailsSection: React.FC<Props> = ({
  reference,
  propertyOwner,
  onReferenceChange,
  onPropertyOwnerChange,
}) => {
  const { t } = useTranslation(['purchase_flow_revamp', 'purchase_flow'])

  return (
    <SectionedBorderBox variant="flat"
      title={t('purchase_flow_revamp:billing.details.section_title')}
      description={t('purchase_flow_revamp:billing.details.section_description')}
    >
      <Stack gap={1.2}>

        <MUIInputField
          label={t('purchase_flow_revamp:billing.details.reference_label')}
          placeholder={t('purchase_flow_revamp:billing.details.reference_placeholder')}
          value={reference ?? ''}
          maxLength={MAX_REFERENCE_CHARACTERS}
          onChange={(e) => onReferenceChange(sanitizeReference(e.target.value) || undefined)}
        />

        {onPropertyOwnerChange &&
          <MUIInputField
            isMultiline
            // The legacy label already says "(Optional)"; its sibling note is a
            // single space in all six locales, so there is nothing to render.
            label={t('purchase_flow:billing_step.property_owner_field_label')}
            value={propertyOwner ?? ''}
            onChange={(e) => onPropertyOwnerChange(e.target.value || undefined)}
          />
        }

      </Stack>
    </SectionedBorderBox>
  )
}
